// src/api/HippocampusAPI.ts
import { BaseAPI } from "./BaseApi";

export class HippocampusAPI extends BaseAPI {
  constructor() {
    super(""); // you can pass REACT_APP_API_URL here if needed
  }

  // User endpoints
  async getCurrentUser() {
    return this.request<{ user: any; config: any; linkedAccounts: string[] }>("/api/users/me", { method: "GET", auth: true });
  }

  async getUsers(username: string) {
    return this.request(`/api/users/search?username=${username}`, {
      method: "GET",
      credentials: "include",
    });
  }

  async getProfile(userId: string) {
    return this.request(`/api/users/profile?userId=${userId}`, {
      method: "GET",
      credentials: "include",
    });
  }

  async updateUserConfig(data: Record<string, any>) {
    return this.request("/api/users/update/config", {
      method: "PATCH",
      body: JSON.stringify(data),
      auth: true,
    });
  }

  async getLinkedAccounts() {
    return this.request("/api/linkedaccounts/me", {
      method: "GET",
      credentials: "include",
    });
  }

  async login(payload: any) {
    return this.request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(payload),
      credentials: "include",
    });
  }

  async logout() {
    return this.request("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  }

  async getGames(userId:string) {
    return this.request(`/api/users/get/games/${userId}`, {
      method: "GET",
      credentials: "include",
    });
  }

  async syncUserGames() {
    return this.request("/api/steam/user/sync", {
      method: "POST",
      auth: true,
    });
  }

  async getSyncStatus() {
    return this.request<{ jobs: Array<{ jobType: string; status: string; startedAt: Date }> }>("/api/steam/sync/status", {
      method: "GET",
      auth: true,
    });
  }

  async registerUser(payload: any) {
    return this.request("/api/users/register", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getVerifyEmail(payload: any) {
    return this.request("/api/auth/verify-email", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  }

  resendVerificationEmail(payload: { email: string }) {
    return this.request<{ message: string }>("/api/auth/resend-verification", {
      method: "POST",
      auth: false,
      body: JSON.stringify(payload),
    });
  }

  async forgotPassword(email: string) {
    return this.request<{ message: string }>("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  }

  // 🖊️ Profile editing endpoints
  async updateProfile(formData: FormData) {
    return this.request<{ user: any }>("/api/users/profile", {
      method: "PATCH",
      body: formData,
      auth: true,
    });
  }

  async setGameVisibility(gameId: string, hidden: boolean) {
    return this.request<{ gameId: string; status: string }>(`/api/users/games/${gameId}/visibility`, {
      method: "PATCH",
      body: JSON.stringify({ hidden }),
      auth: true,
    });
  }

  // 🎨 Avatar selection endpoints
  async selectAvatar(avatarId: string) {
    return this.request<{ profileImageUrl: string }>("/api/users/avatar/select", {
      method: "POST",
      body: JSON.stringify({ avatarId }),
      auth: true,
    });
  }

  async getDiscordMembership() {
    return this.request<{ linked: boolean; isMember: boolean; error?: string }>("/api/discord/membership-status", {
      method: "GET",
      auth: true,
    });
  }

  async rejoinDiscord() {
    return this.request<{ linked: boolean; isMember: boolean; error?: string; reauthRequired?: boolean }>("/api/discord/rejoin", {
      method: "POST",
      auth: true,
    });
  }

  async unlinkDiscord() {
    return this.request<{ message: string }>("/api/discord/unlink", {
      method: "DELETE",
      auth: true,
    });
  }

  async unlinkEpic() {
    return this.request<{ message: string }>("/api/epic/unlink", {
      method: "DELETE",
      auth: true,
    });
  }

  async unlinkSteam() {
    return this.request<{ message: string }>("/api/steam/unlink", {
      method: "DELETE",
      auth: true,
    });
  }

}
